import { Sparkline } from "@/components/Sparkline";
import { ArrowUp, ArrowDown } from "lucide-react";

/**
 * MetricTile — the larger, mockup-spec dashboard tile.
 *
 * Layout (top → bottom):
 *   • Header row:  small uppercase label · top-right colored dot (source
 *                  indicator: green = direct endpoint, amber = aggregated)
 *   • Value row:   big numeric value (RAG-coloured), optional ▲/▼ delta line
 *   • Sparkline:   full-width thin sparkline (line chart, no fill)
 *   • Footer row:  left muted "target …" / context text · right bordered
 *                  status pill (HEALTHY / AT RISK / BREACH / OK / INFO)
 *
 * Visual tokens are lifted from MasterDashboard.js + the existing tiles
 * (`bg-white`, hairline border, IBM Plex Mono numerals, Chivo uppercase
 * labels) so this stays consistent with the rest of the app.
 */
const RAG_TONE = {
  green: "text-green-700",
  amber: "text-amber-700",
  red: "text-red-600",
  neutral: "text-zinc-900",
};

const PILL_TONE = {
  HEALTHY: "border-green-600 text-green-700 bg-green-50",
  OK:      "border-green-600 text-green-700 bg-green-50",
  "AT RISK": "border-amber-500 text-amber-700 bg-amber-50",
  BREACH:  "border-red-600 text-red-600 bg-red-50",
  INFO:    "border-zinc-300 text-zinc-600 bg-white",
};

export function MetricTile({
  label,
  value,
  delta,           // { sign: 1|-1|0, label: "+4.0 pts" } or null
  rag = "neutral",
  source = "direct", // "direct" | "aggregated"
  sparkline,       // { values: number[], type: "neutral"|"cost"|"sp"|"margin" }
  footerLeft,      // muted text, e.g. "target 95%" / "this month"
  status,          // pill key: HEALTHY / OK / AT RISK / BREACH / INFO
  subText,         // optional secondary line under value (e.g. "₹1,016 realised")
  onClick,
  testId,
}) {
  const valueTone = RAG_TONE[rag] || RAG_TONE.neutral;
  const pillCls = PILL_TONE[status] || PILL_TONE.INFO;
  const sign = delta?.sign ?? 0;
  const DeltaIcon = sign > 0 ? ArrowUp : sign < 0 ? ArrowDown : null;
  const deltaTone = sign > 0 ? "text-green-700" : sign < 0 ? "text-red-600" : "text-muted-foreground";
  const sourceDot = source === "direct" ? "bg-green-500" : "bg-amber-500";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || "metric-tile"}
      className="group bg-white p-8 text-left w-full flex flex-col gap-5 min-h-[260px] hover:bg-zinc-50/60 transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
          {label}
        </div>
        <span className={`h-2 w-2 rounded-full ${sourceDot} shrink-0 mt-1`} aria-hidden />
      </div>

      {/* Value + delta */}
      <div className="flex-1 flex flex-col gap-2">
        <div
          className={`text-5xl font-black font-mono leading-none ${valueTone}`}
          data-testid={`${testId}-value`}
        >
          {value ?? "—"}
        </div>
        {delta && (
          <div className={`flex items-center gap-1 text-xs font-mono ${deltaTone}`}>
            {DeltaIcon && <DeltaIcon className="w-3 h-3" strokeWidth={2} />}
            <span>{delta.label}</span>
          </div>
        )}
        {subText && (
          <div className="text-xs font-mono text-zinc-700" data-testid={`${testId}-sub`}>
            {subText}
          </div>
        )}
        {sparkline && (
          <div className="pt-2">
            <Sparkline
              values={sparkline.values}
              type={sparkline.type || "neutral"}
              w={220}
              h={36}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-end justify-between gap-3 pt-1">
        <div className="text-[11px] font-mono text-muted-foreground tracking-wide">
          {footerLeft || ""}
        </div>
        {status && (
          <span
            className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2.5 py-1 border ${pillCls}`}
            data-testid={`${testId}-pill`}
          >
            {status}
          </span>
        )}
      </div>
    </button>
  );
}

export default MetricTile;
